import { useEffect, useCallback } from 'react';
import useNavHeight from './useNavHeight';

const useScrollSticky = element => {
  const navHeight = useNavHeight(element);
  const convertToInt = style => Number(style.replace('px', ''));

  const handleScroll = useCallback(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleScroll);
    const elemHeight = element?.current?.getBoundingClientRect().height;
    // If the diff is negative, then the scroll direction is down
    // If the diff is positive, then the scroll direction is up
    const diff = element?.current?.prev - window.pageYOffset || 0;

    // Different limits for scrolling up vs down
    const max = diff < 0 ? window.innerHeight - elemHeight - 6 : navHeight + 6;

    if (
      diff &&
      convertToInt(element?.current?.style?.top) !== max &&
      window.innerHeight - navHeight < elemHeight
    ) {
      const absDiff = Math.abs(diff);
      const diffFromMax =
        Math.sign(convertToInt(element?.current?.style?.top) - max) * absDiff;

      // Calculate new offset based on position relative to the max limit (above vs below)
      const newTop =
        diffFromMax < 0
          ? Math.min(max, convertToInt(element?.current?.style?.top) + absDiff)
          : Math.max(max, convertToInt(element?.current?.style?.top) - absDiff);
      if (newTop !== convertToInt(element?.current?.style?.top)) {
        element.current.style.top = `${newTop}px`;
      }
    }
  }, [element, navHeight]);

  // This function handles scroll performance issues
  const debounce = useCallback(
    (func, wait: number = 10, immediate: boolean = true) => {
      window.removeEventListener('scroll', handleScroll);
      let timeOut;
      return () => {
        const context = this;
        const args = { func, wait, immediate };
        const later = () => {
          timeOut = null;
          if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeOut;
        clearTimeout(timeOut);
        timeOut = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    },
    [handleScroll]
  );

  useEffect(() => {
    const onScroll = e => {
      e.stopPropagation();
      debounce(handleScroll());
      element.current.prev = window.pageYOffset;
    };

    if (element && element.current && Number.isInteger(navHeight)) {
      window.addEventListener('scroll', onScroll);
      window.addEventListener('resize', onScroll);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [element, debounce, handleScroll, navHeight]);
};

export default useScrollSticky;
